import type {
  ExcelGridCellCommit,
  ExcelGridSelectEditorConfig,
  ExcelGridSelectOption,
} from "../../shared/excel-grid/models/excel-grid.models";
import type {
  AdminCrewReportApiErrorResponse,
  AdminCrewReportRecipientResolutionStatus,
  AdminCrewReportRequestFeedback,
  AdminCrewReportRequestResponse,
  AdminCrewReportUploadOptionsResponse,
  AdminCrewReportUploadPayloadRow,
  AdminCrewReportUploadPreview,
  AdminCrewReportUploadRow,
  AdminCrewReportVesselOption,
} from "../models/admin-crew-report.models";
import {
  getAdminCrewReportImoFromFileName,
  getAdminCrewReportWorkbookColumnOffset,
} from "./admin-crew-report-workbook-layout.utils";
import {
  ADMIN_CREW_REPORT_UPLOAD_MAX_ROWS,
  CREW_REPORT_UPLOAD_SHEET_NAME,
  CREW_REPORT_UPLOAD_FIRST_DATA_ROW_INDEX,
  CREW_REPORT_UPLOAD_MAX_LAYOUT_COLUMN_COUNT,
  CREW_REPORT_UPLOAD_FILE_EXTENSION,
  CREW_REPORT_UPLOAD_MAX_FILE_SIZE_MB,
  CREW_REPORT_UPLOAD_MAX_FILE_SIZE_BYTES,
  CREW_REPORT_UPLOAD_PARSE_TIMEOUT_MS,
  EXCEL_DATE_UNIX_EPOCH_SERIAL,
  MILLISECONDS_PER_DAY,
  CREW_REPORT_UPLOAD_COLUMNS,
} from "./admin-crew-report.constants";
import axios from "axios";

export const ADMIN_CREW_REPORT_VESSEL_ROW_HEIGHT = 58;
export const ADMIN_CREW_REPORT_VESSEL_LIST_MAX_HEIGHT = 290;

export function getCrewReportRequestButtonLabel(
  isSending: boolean,
  recipientStatus: AdminCrewReportRecipientResolutionStatus,
): string {
  if (isSending) {
    return "Sending";
  }

  if (recipientStatus === "resolving") {
    return "Resolving recipient";
  }

  return "Request Crew Report";
}

export function getCrewReportSendConfirmationMessage(vesselName: string, email: string): string {
  return `Send email request to ${vesselName} - ${email}?`;
}

export function getAdminCrewReportRequestFeedback(
  response: AdminCrewReportRequestResponse,
): AdminCrewReportRequestFeedback {
  if (response.emailDeliveryConfirmed === false) {
    return {
      status: "sentWithWarning",
      message: `The Crew Report Request ID was saved for ${response.sentToVessel}, but email delivery to ${response.sentTo} could not be confirmed. Do not resend immediately; replies using this Request ID will still be accepted. Verify delivery with the vessel before sending a replacement.`,
    };
  }

  if (response.requestStatusRecorded === false) {
    return {
      status: "sentWithWarning",
      message: `Crew Report request sent to ${response.sentTo} for ${response.sentToVessel}, but the request status was not saved. Do not resend only because of this warning.`,
    };
  }

  return {
    status: "sent",
    message: `Crew Report request sent to ${response.sentTo} for ${response.sentToVessel}.`,
  };
}

export function getCrewReportVesselSelectorPlaceholder(loading: boolean, vesselCount: number): string {
  if (loading) {
    return "Loading active vessels";
  }

  if (vesselCount === 0) {
    return "No active vessels";
  }

  return "Select a vessel";
}

export function getAdminCrewReportVesselKey(vessel: AdminCrewReportVesselOption): string {
  return `${vessel.vesselCode}:${vessel.imoNumber}`;
}

export function getFilteredAdminCrewReportVessels(
  vessels: AdminCrewReportVesselOption[],
  inputValue: string,
  selectedVessel: AdminCrewReportVesselOption | undefined,
): AdminCrewReportVesselOption[] {
  const normalizedSearchTerm = inputValue.trim().toLowerCase();

  if (!normalizedSearchTerm || selectedVessel?.vesselName === inputValue) {
    return vessels;
  }

  return vessels.filter((vessel) =>
    [vessel.vesselName, vessel.vesselCode, vessel.imoNumber, vessel.vesselType].some((value) =>
      value?.toLowerCase().includes(normalizedSearchTerm),
    ),
  );
}

export function getAdminCrewReportErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as AdminCrewReportApiErrorResponse | string | undefined;

    if (typeof responseData === "string") {
      return responseData;
    }

    return responseData?.error ?? responseData?.message ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Crew Report request failed.";
}

export function getAdminCrewReportErrorDetails(error: unknown): string[] {
  if (!axios.isAxiosError(error)) {
    return [];
  }

  const responseData = error.response?.data as AdminCrewReportApiErrorResponse | string | undefined;

  if (!responseData || typeof responseData === "string" || !Array.isArray(responseData.errors)) {
    return [];
  }

  return responseData.errors.filter(
    (detail): detail is string => typeof detail === "string" && detail.trim().length > 0,
  );
}

export async function parseAdminCrewReportUploadFile(file: File): Promise<AdminCrewReportUploadPreview> {
  validateAdminCrewReportUploadFile(file);
  getAdminCrewReportImoFromFileName(file.name);

  const worksheetRows = await readAdminCrewReportUploadWorksheetRows(file);

  return createAdminCrewReportUploadPreviewFromWorksheetRows(file.name, worksheetRows);
}

export function createAdminCrewReportUploadPreviewFromWorksheetRows(
  fileName: string,
  worksheetRows: unknown[][],
): AdminCrewReportUploadPreview {
  getAdminCrewReportImoFromFileName(fileName);
  const offset = getAdminCrewReportWorkbookColumnOffset(worksheetRows[0] ?? []);
  const rows = worksheetRows
    .slice(CREW_REPORT_UPLOAD_FIRST_DATA_ROW_INDEX)
    .map((rowValues, rowIndex) => createAdminCrewReportUploadRow(rowValues.slice(offset), rowIndex))
    .filter(hasCrewReportUploadRowValue);

  if (rows.length === 0) {
    throw new Error(`The "${CREW_REPORT_UPLOAD_SHEET_NAME}" worksheet does not contain crew rows to preview.`);
  }

  if (rows.length > ADMIN_CREW_REPORT_UPLOAD_MAX_ROWS) {
    throw new Error(`Crew Report upload must contain ${ADMIN_CREW_REPORT_UPLOAD_MAX_ROWS} rows or fewer.`);
  }

  return {
    fileName,
    sheetName: CREW_REPORT_UPLOAD_SHEET_NAME,
    columns: CREW_REPORT_UPLOAD_COLUMNS.map(({ dateColumn: _dateColumn, ...column }) => ({
      ...column,
      editable: true,
      filterable: true,
      sortable: true,
    })),
    rows,
  };
}

export function applyAdminCrewReportUploadSelectEditors(
  preview: AdminCrewReportUploadPreview,
  options: AdminCrewReportUploadOptionsResponse,
): AdminCrewReportUploadPreview {
  const genderEditor = createAdminCrewReportUploadSelectEditor(options.genderValues, "Select gender");
  const nationalityEditor = createAdminCrewReportUploadSelectEditor(
    options.nationalityValues,
    "Select nationality",
    true,
  );
  const designationEditor = createAdminCrewReportUploadSelectEditor(
    options.designationValues,
    "Select designation",
    true,
  );

  return {
    ...preview,
    columns: preview.columns.map((column) => {
      if (column.key === "gender") {
        return { ...column, editor: genderEditor };
      }

      if (column.key === "nationality") {
        return { ...column, editor: nationalityEditor };
      }

      if (column.key === "designationRank") {
        return { ...column, editor: designationEditor };
      }

      return column;
    }),
    rows: preview.rows.map((row) => ({
      ...row,
      gender: getCanonicalAdminCrewReportUploadSelectValue(row.gender, options.genderValues),
      nationality: row.nationality.trim(),
      designationRank: row.designationRank.trim(),
    })),
  };
}

export function applyAdminCrewReportUploadCellCommit(
  rows: AdminCrewReportUploadRow[],
  commit: ExcelGridCellCommit,
): AdminCrewReportUploadRow[] {
  return rows.map((row) =>
    row.gridRowId === commit.row.gridRowId
      ? ({ ...row, [commit.column.key]: commit.value.trim() } as AdminCrewReportUploadRow)
      : row,
  );
}

export function toAdminCrewReportUploadPayloadRows(
  rows: AdminCrewReportUploadRow[],
): AdminCrewReportUploadPayloadRow[] {
  return rows.map((row) => ({
    gender: row.gender,
    nationality: row.nationality,
    designationRank: row.designationRank,
    contractDurationMonths: row.contractDurationMonths,
    signOnDate: row.signOnDate,
    changePlannedDate: row.changePlannedDate,
  }));
}

export function getAdminCrewReportUploadErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return "Crew Report upload preview failed.";
}

function createAdminCrewReportUploadRow(rowValues: unknown[], rowIndex: number): AdminCrewReportUploadRow {
  return CREW_REPORT_UPLOAD_COLUMNS.reduce<AdminCrewReportUploadRow>(
    (row, column, columnIndex) => {
      row[column.key] = formatAdminCrewReportUploadCellValue(rowValues[columnIndex], column.dateColumn);
      return row;
    },
    { gridRowId: `crew-report-upload-row-${rowIndex + 1}` } as AdminCrewReportUploadRow,
  );
}

function hasCrewReportUploadRowValue(row: AdminCrewReportUploadRow): boolean {
  return CREW_REPORT_UPLOAD_COLUMNS.some((column) => row[column.key].trim().length > 0);
}

function createAdminCrewReportUploadSelectEditor(
  values: string[],
  placeholder: string,
  allowCustomValue = false,
): ExcelGridSelectEditorConfig {
  return {
    type: "select",
    options: toAdminCrewReportUploadSelectOptions(values),
    allowCustomValue,
    isSearchable: true,
    placeholder,
  };
}

function toAdminCrewReportUploadSelectOptions(values: string[]): ExcelGridSelectOption[] {
  return values.map((value) => ({ label: value, value }));
}

function getCanonicalAdminCrewReportUploadSelectValue(value: string, values: string[]): string {
  const lookupKey = toAdminCrewReportUploadSelectLookupKey(value);

  if (!lookupKey) {
    return value;
  }

  return values.find((allowedValue) => toAdminCrewReportUploadSelectLookupKey(allowedValue) === lookupKey) ?? value;
}

function toAdminCrewReportUploadSelectLookupKey(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function formatAdminCrewReportUploadCellValue(value: unknown, isDateColumn = false): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (isDateColumn) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return formatExcelSerialDate(value);
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return formatUtcCalendarDate(value);
    }
  }

  return String(value).trim();
}

function validateAdminCrewReportUploadFile(file: File): void {
  if (!file.name.toLowerCase().endsWith(CREW_REPORT_UPLOAD_FILE_EXTENSION)) {
    throw new Error("Crew Report upload only supports .xlsx files.");
  }

  if (file.size > CREW_REPORT_UPLOAD_MAX_FILE_SIZE_BYTES) {
    throw new Error(`Crew Report upload file must be ${CREW_REPORT_UPLOAD_MAX_FILE_SIZE_MB} MB or smaller.`);
  }
}

async function readAdminCrewReportUploadWorksheetRows(file: File): Promise<unknown[][]> {
  return withAdminCrewReportUploadParseTimeout(async () => {
    const workbookArrayBuffer = await file.arrayBuffer();
    const { readSheet } = await import("read-excel-file/browser");
    const worksheetRows = await readSheet(workbookArrayBuffer, CREW_REPORT_UPLOAD_SHEET_NAME);

    return worksheetRows.map((row) => toVisibleWorksheetRow(row, CREW_REPORT_UPLOAD_MAX_LAYOUT_COLUMN_COUNT));
  });
}

function withAdminCrewReportUploadParseTimeout<T>(parseWorkbook: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Crew Report upload preview timed out."));
    }, CREW_REPORT_UPLOAD_PARSE_TIMEOUT_MS);

    parseWorkbook()
      .then(resolve)
      .catch((error) => {
        reject(error instanceof Error ? error : new Error("Crew Report upload preview failed."));
      })
      .finally(() => window.clearTimeout(timeoutId));
  });
}

function toVisibleWorksheetRow(row: readonly unknown[], visibleColumnCount: number): unknown[] {
  return Array.from({ length: visibleColumnCount }, (_, columnIndex) => row[columnIndex] ?? "");
}

function formatExcelSerialDate(serialDate: number): string {
  const date = new Date((serialDate - EXCEL_DATE_UNIX_EPOCH_SERIAL) * MILLISECONDS_PER_DAY);
  return formatUtcCalendarDate(date);
}

function formatUtcCalendarDate(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${month}/${day}/${date.getUTCFullYear()}`;
}
